import type { ReactNode } from 'react';
import { useContext, useMemo, createContext, useCallback, useState, useRef } from 'react';
import GenericModal from 'components/modal/GenericModal';
import lang from 'vars/lang';

type ConfirmationContextType = {
  showConfirmationModal: (params: ConfirmationModalParams) => Promise<boolean>;
};

export type ConfirmationModalParams = {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  rejectText?: string;
};

export const ConfirmationContext = createContext<ConfirmationContextType | null>(null);

export const ConfirmationProvider = ({ children }: { children: ReactNode }) => {
  const [modalState, setModalState] = useState<ConfirmationModalParams>();
  const resolverRef = useRef<((res: boolean) => void) | null>(null);

  const showConfirmationModal = useCallback(async (params: ConfirmationModalParams) => {
    setModalState(params);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const cleanup = useCallback(() => {
    setModalState(undefined);
    resolverRef.current = null;
  }, []);

  const handleModalClose = useCallback(() => {
    resolverRef.current?.(false);
    cleanup();
  }, [cleanup]);

  const handleConfirm = useCallback(() => {
    resolverRef.current?.(true);
    cleanup();
  }, [cleanup]);

  const value = useMemo(() => ({ showConfirmationModal }), [showConfirmationModal]);

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
      {!!modalState && (
        <GenericModal
          onClose={handleModalClose}
          title={modalState.title}
          description={modalState.description}
          submitButtonText={modalState.confirmText ?? lang.generics.confirm}
          cancelButtonText={modalState.rejectText ?? lang.generics.cancel}
          handleSubmitClick={handleConfirm}
          size="tiny"
        />
      )}
    </ConfirmationContext.Provider>
  );
};

export function useConfirmation() {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error('useConfirmation must be used within ConfirmationProvider');
  }

  return context;
}
